import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { deleteContact } from "@/api/contacts";
import type { Company, Contact, EmbeddedContact } from "@/api/types";

interface DeleteContactDialogProps {
  projectSlug: string;
  contact: Contact | EmbeddedContact | null;
  company: Company | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: (id: number) => void;
}

export function DeleteContactDialog({
  projectSlug,
  contact,
  company,
  open,
  onOpenChange,
  onDeleted,
}: DeleteContactDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  async function handleDelete() {
    if (!contact) return;
    setSubmitting(true);
    try {
      await deleteContact(projectSlug, contact.id);
      toast.success(`${contact.full_name} removed`);
      onDeleted(contact.id);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Delete this contact?</DialogTitle>
          <DialogDescription>
            {contact && (
              <>
                <span className="font-medium text-slate-900">
                  {contact.full_name}
                </span>
                {company && (
                  <>
                    {" "}
                    at{" "}
                    <span className="font-medium text-slate-900">
                      {company.name}
                    </span>
                  </>
                )}
                .
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
          <p className="text-xs leading-relaxed text-rose-700">
            This action is irreversible. Email, phone, LinkedIn and any related
            activity for this contact will be removed.
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={submitting || !contact}
          >
            {submitting && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
  }
  return "Couldn't delete the contact.";
}
